import { useEffect, useState } from "react";
import { getClassViewService } from "../api/landingClass.service";
import { mapViewToCourse, courseToListSnapshot } from "../api/landingClass.mappers";
import type { ClassListSnapshot } from "../api/landingClass.types";
import type { LandingCourse } from "../types/landing.types";
import { saveClassDetailSnapshot } from "../utils/classDetailSnapshot";
import { resolveClassListSnapshot } from "../utils/resolveClassListSnapshot";

function needsListSnapshotFallback(
  snapshot: ClassListSnapshot | null
): boolean {
  if (!snapshot) return true;
  return snapshot.units === 0 && snapshot.videos === 0;
}

interface UseClassViewOptions {
  classId: string;
  levelId: string;
  level1Id?: string;
  snapshot?: ClassListSnapshot | null;
  catalogSlugs?: { categorySlug?: string; courseSlug?: string };
}

interface UseClassViewResult {
  course: LandingCourse | null;
  isLoading: boolean;
  error: string | null;
}

export function useClassView({
  classId,
  levelId,
  level1Id,
  snapshot,
  catalogSlugs,
}: UseClassViewOptions): UseClassViewResult {
  const [course, setCourse] = useState<LandingCourse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const snapshotKey = snapshot ? JSON.stringify(snapshot) : "";

  useEffect(() => {
    if (!classId) {
      setCourse(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getClassViewService(classId);
        if (cancelled) return;
        const data = response.data;
        if (!data) {
          setCourse(null);
          setError("Class not found.");
          return;
        }
        let parsedSnapshot: ClassListSnapshot | null = snapshotKey
          ? (JSON.parse(snapshotKey) as ClassListSnapshot)
          : null;

        if (needsListSnapshotFallback(parsedSnapshot) && levelId) {
          const fromList = await resolveClassListSnapshot(
            levelId,
            classId,
            level1Id
          );
          if (fromList && !cancelled) {
            parsedSnapshot = fromList;
            saveClassDetailSnapshot(levelId, classId, fromList);
          }
        }

        if (cancelled) return;
        const mapped = mapViewToCourse(
          data,
          levelId,
          parsedSnapshot,
          catalogSlugs
        );
        setCourse(mapped);

        if (levelId && classId) {
          saveClassDetailSnapshot(levelId, classId, courseToListSnapshot(mapped));
        }
      } catch {
        if (cancelled) return;
        setCourse(null);
        setError("Unable to load class details.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [classId, levelId, level1Id, snapshotKey, catalogSlugs?.categorySlug, catalogSlugs?.courseSlug]);

  return { course, isLoading, error };
}
